import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import searchContents from "@tSchaedlerYesco/Handlers/ContentSearchHandler";
import setActiveTab from "@tSchaedlerYesco/Handlers/SearchResultsTabsHandler";
import CustomApplicationState from "@tSchaedlerYesco/Store/CustomApplicationState";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState & CustomApplicationState) => {
    const { productFilters } = state.pages.productList;
    const pagination = getProductListDataViewProperty(state, "pagination");
    const {
        pages: {
            productList: { isSearchPage },
        },
    } = state;

    const {
        contentSearch: { contents, isLoading, requestCompleted, query },
        brandSearch: { brands },
        searchResultsTab,
    } = state.tData;
    return {
        query: productFilters.query,
        location: getLocation(state),
        isSearchPage,
        activeTab: searchResultsTab.activeTab,
        totalItemCount: pagination?.totalItemCount,
        contents,
        brands,
        isLoading,
        requestCompleted,
        contentsQuery: query,
    };
};

const mapDispatchToProps = {
    setActiveTab,
    searchContents,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface ProductListViewSelectStyles {
    wrapper?: InjectableCss;
    tabButton?: InjectableCss;
    spacing?: InjectableCss;
    results?: TypographyProps;
    spacing1?: InjectableCss;
    articleButton?: InjectableCss;
    BTNspace?: InjectableCss;
}

export const viewSelectStyles: ProductListViewSelectStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin-bottom: 10px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        gap: 5px;
                        margin-left: 5px;
                        margin-right: 5px;
                    `,
                    css`
                        gap: 5px;
                        margin-left: 5px;
                        margin-right: 5px;
                    `,
                    css`
                        gap: 5px;
                        margin-left: 10px;
                        margin-right: 10px;
                    `,
                    null,
                    null,
                ])}
            .activeTab {
                background-color: ${getColor("primary")};
                > span {
                    color: white;
                }
            }
        `,
    },
    tabButton: {
        css: css`
            border: 1px solid grey;
            width: 100%;
            color: ${getColor("primary")};
            background: rgba(17, 41, 69, 0.027);
            text-transform: uppercase;
            padding: 15px 0;
            border-radius: 0;
            span {
                font-weight: bold;
                color: ${getColor("primary")};
            }
            &:hover {
                background-color: ${getColor("primary")};
                border: 1px solid ${getColor("secondary")};
                span {
                    color: white;
                }
            }
            &:focus {
                outline: none;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        min-height: 35px;
                        padding: 0px 20px;
                        border-radius: 25px;
                        span {
                        }
                    `,
                    css`
                        padding: 0px 20px;
                        border-radius: 25px;
                        span {
                        }
                    `,
                    css`
                        padding: 0px 80px;
                        border-radius: 25px;
                        span {
                        }
                    `,
                    css`
                        padding: 15px 135px;
                        span {
                        }
                    `,
                ])}
        `,
    },
    spacing: {
        css: css`
            flex: 1;
        `,
    },
    results: {
        css: css`
            color: #275aa8;
            font-weight: bold;
            margin-right: 20px;
        `,
    },
    spacing1: {
        css: css`
            flex: 1;
            text-align: end;
            margin-top: 10px;
        `,
    },
    articleButton: {
        css: css`
            border: 1px solid grey;
            color: ${getColor("primary")};
            background: rgba(17, 41, 69, 0.027);
            width: 100%;
            padding: 15px 0;
            border-radius: 0;
            span {
                color: ${getColor("primary")};
                font-weight: bold;
            }
            &:hover {
                background-color: ${getColor("primary")};
                border: 1px solid ${getColor("secondary")};
                span {
                    color: white;
                }
            }
            &:focus {
                outline: none;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        min-height: 35px;
                        padding: 0px 10px;
                        border-radius: 25px;
                        span {
                        }
                    `,
                    css`
                        padding: 0px 15px;
                        border-radius: 25px;
                        span {
                        }
                    `,
                    css`
                        padding: 0px 60px;
                        border-radius: 25px;
                        span {
                        }
                    `,
                    css`
                        padding: 15px 135px;
                        span {
                        }
                    `,
                ])}
        `,
    },
};

const styles = viewSelectStyles;

const SearchPageTabs: FC<Props> = ({
    location,
    history,
    isSearchPage,
    query,
    activeTab,
    setActiveTab,
    totalItemCount,
    contents,
    brands,
    searchContents,
}) => {
    useEffect(() => {
        const queryParams = parseQueryString<{ activeTab?: string }>(window.location.search);
        if (queryParams.activeTab) {
            setActiveTab({
                activeTab: queryParams.activeTab,
            });
        }
    }, []);

    useEffect(() => {
        searchContents({
            query,
        });
    }, []);

    const handleChangeTab = (value: string) => {
        const queryParams = parseQueryString<any>(location.search);
        if (queryParams.activeTab === value) {
            return;
        }
        setActiveTab({
            activeTab: value,
        });
        queryParams.activeTab = value;
        const newUrl = Object.keys(queryParams)
            .map((key: string) => `${key}=${queryParams[key]}`)
            .join("&");
        history.push(`${location.pathname}?${newUrl}`);
    };

    if (!isSearchPage) {
        return null;
    }
    const pdfContent = contents?.filter(item => item.url?.toLowerCase().endsWith(".pdf")) || [];
    const nonPdfContent = contents?.filter(item => !item.url?.toLowerCase().endsWith(".pdf")) || [];

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.spacing1}>
                <Typography {...styles.results}>{translate("Search Results ")}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.spacing}>
                <Button
                    {...styles.tabButton}
                    className={activeTab === "products" ? "activeTab" : ""}
                    onClick={() => handleChangeTab("products")}
                >
                    {`${translate("PRODUCTS")} (${totalItemCount})`}
                </Button>
            </StyledWrapper>

            <StyledWrapper {...styles.spacing}>
                <Button
                    {...styles.articleButton}
                    className={activeTab === "brand" ? "activeTab" : ""}
                    onClick={() => handleChangeTab("brand")}
                >
                    {`${translate("BRANDS")} (${brands.length})`}
                </Button>
            </StyledWrapper>

            {/* <StyledWrapper {...styles.spacing}>
                <Button
                    {...styles.tabButton}
                    className={activeTab === "categories" ? "activeTab" : ""}
                    onClick={() => handleChangeTab("categories")}
                >
                    {translate("categories")}
                </Button>
            </StyledWrapper> */}

            <StyledWrapper {...styles.spacing}>
                <Button
                    {...styles.articleButton}
                    className={activeTab === "content" ? "activeTab" : ""}
                    onClick={() => handleChangeTab("content")}
                >
                    {`${translate("PAGES")} (${nonPdfContent.length})`}
                </Button>
            </StyledWrapper>

            <StyledWrapper {...styles.spacing}>
                <Button
                    {...styles.articleButton}
                    className={activeTab === "doc" ? "activeTab" : ""}
                    onClick={() => handleChangeTab("doc")}
                >
                    {`${translate("DOCUMENTS")} (${pdfContent.length})`}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(SearchPageTabs)),
    definition: {
        group: "Product List",
        displayName: "searchTab",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
